import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { computePeacMonthly, PLM_MAX_RATIO } from './peacLeasing';
import './ConfiguratorAriane.css';

/**
 * Configurateur pour le Laser Ariane
 * Permet de sélectionner un modèle de machine (Illimité ou Connect),
 * de choisir entre achat et location, et d'ajouter des options.
 */
function ConfiguratorAriane() {
  // ============================================
  // ÉTATS (STATE)
  // ============================================

  // Machine sélectionnée (pam4-illimite, pam4-connect, pam2-illimite, pam2-connect)
  const [selectedMachine, setSelectedMachine] = useState('pam4-illimite');

  // Type d'achat : 'purchase' (achat comptant) ou 'lease' (location)
  const [purchaseType, setPurchaseType] = useState('purchase');

  // Durée de location : '48' mois ou '60' mois
  const [leaseDuration, setLeaseDuration] = useState('48');

  // Premier loyer majoré (montant déduit de la base pour réduire les mensualités)
  const [firstLeasePayment, setFirstLeasePayment] = useState(0);

  // Affichage du détail du prix dans la barre de résumé
  const [isDetailsExpanded, setIsDetailsExpanded] = useState(false);

  // Index de l'image/vidéo actuellement affichée dans le carrousel
  const [currentImageIndex, setCurrentImageIndex] = useState(0);

  // Pack de tirs laser sélectionné (uniquement pour modèles Connect)
  const [selectedShotPack, setSelectedShotPack] = useState(null);

  // Nombre de personnes pour la formation IPL
  const [formationIPLCount, setFormationIPLCount] = useState(1);

  // Nombre de personnes pour la formation Laser
  const [formationLaserCount, setFormationLaserCount] = useState(1);

  // Pack de flashs IPL sélectionné (visible si applicateur HR ou SR coché)
  const [selectedFlashPack, setSelectedFlashPack] = useState(null);

  // Options sélectionnées (checkboxes)
  // Les clés "xxxMonthly" indiquent si l'option est payée mensuellement
  const [selectedOptions, setSelectedOptions] = useState({
    warranty: false,
    warrantyMonthly: false,
    applicateurHR: false,
    applicateurHRMonthly: false,
    applicateurSR: false,
    applicateurSRMonthly: false,
    emboutIPLV64: false,
    installation: false,
    formationLaser: false,
    formationLaserIPL: false
  });

  // ============================================
  // DONNÉES STATIQUES
  // ============================================

  // Médias du carrousel (vidéos YouTube et images)
  const media = [
    { type: 'video', url: 'https://www.youtube.com/embed/VdztHSC6Ivo?start=11&autoplay=1&mute=1&loop=1&playlist=VdztHSC6Ivo&controls=0&modestbranding=1&rel=0&showinfo=0&iv_load_policy=3' }
    // { type: 'image', url: 'https://placehold.co/1780x900' },
    // { type: 'image', url: 'https://placehold.co/1780x900' },
    // { type: 'image', url: 'https://placehold.co/1780x900' }
  ];

  /**
   * Options disponibles pour le configurateur
   * - name: Nom affiché
   * - price: Prix comptant
   * - monthlyPrice: Prix mensuel (si hasMonthlyOption = true)
   * - hasMonthlyOption: Possibilité de payer en mensualités
   * - includedInLease: Option incluse automatiquement en mode location
   * - perPerson: Prix multiplié par le nombre de personnes (formations)
   * - description: Description affichée sous l'option
   */
  const options = {
    warranty: {
      name: 'Extension garantie Laser Ariane 5 ans (2+3)**',
      price: 3000,
      monthlyPrice: 50,
      monthlyDuration: '5 ans',
      hasMonthlyOption: true,
      includedInLease: true
    },
    applicateurHR: {
      name: 'Applicateur HR Legacy ***',
      price: 2500,
      monthlyPrice: 50,
      monthlyDuration: '5 ans',
      hasMonthlyOption: true,
      includedInLease: false,
      description: 'Applicateur HR (hair removal) : pour des soins photo pilaires d\'excellence (efficace sur tous les types de poils et tous les phototypes) et brevet exclusif poils blonds et blancs garantissant l\'élimination des poils clairs et peu pigmentés.'
    },
    applicateurSR: {
      name: 'Applicateur SR Legacy ***',
      price: 2500,
      monthlyPrice: 50,
      monthlyDuration: '5 ans',
      hasMonthlyOption: true,
      includedInLease: false,
      description: 'Applicateur SR (skin rejuvenation) : soins de photorajeunissement (soin anti-âge, anti-taches et anti-rougeurs capillaires).'
    },
    emboutIPLV64: {
      name: 'Embout IPL Petite Zone V64',
      price: 1155,
      hasMonthlyOption: false,
      includedInLease: false,
      description: 'Embout effilé pour traiter les petites zones en soin photo-pilaires et de photorajeunissement (oreilles, nez...).'
    },
    installation: {
      name: 'Installation Laser Ariane',
      price: 490,
      hasMonthlyOption: false,
      includedInLease: true
    },
    formationLaser: {
      name: 'Formation socle Laser',
      price: 1500,
      hasMonthlyOption: false,
      includedInLease: true,
      perPerson: true
    },
    formationLaserIPL: {
      name: 'Formation socle Laser + IPL',
      price: 1900,
      hasMonthlyOption: false,
      includedInLease: false,
      perPerson: true
    }
  };

  // Packs de tirs laser supplémentaires (uniquement pour modèles Connect)
  const shotPacks = {
    '5000': { name: '5 000 tirs', price: 450 },
    '10000': { name: '10 000 tirs', price: 800 },
    '15000': { name: '15 000 tirs', price: 1050 },
    '30000': { name: '30 000 tirs', price: 1800 },
    '60000': { name: '60 000 tirs', price: 3000 },
    '100000': { name: '100 000 tirs', price: 4000 }
  };

  // Packs de flashs IPL (visible si applicateur HR ou SR sélectionné)
  const flashPacks = {
    '5000': { name: '5 000 flashs', price: 850 },
    '10000': { name: '10 000 flashs', price: 1200 },
    '15000': { name: '15 000 flashs', price: 1500 },
    '20000': { name: '20 000 flashs', price: 1800 },
    '30000': { name: '30 000 flashs', price: 2700 },
    '45000': { name: '45 000 flashs', price: 4050 },
    '60000': { name: '60 000 flashs', price: 4800 },
    '75000': { name: '75 000 flashs', price: 6000 }
  };

  /**
   * Machines disponibles
   * - Illimité : tirs laser illimités
   * - Connect : tirs limités avec packs supplémentaires disponibles
   * - isConnect: permet d'afficher les sections spécifiques aux modèles Connect
   * - price: prix d'achat comptant
   * - leaseBasePrice: prix de base pour le calcul des mensualités de location
   */
  const machines = {
    'pam4-illimite': {
      name: 'LASER ARIANE + PAM LASER 4 ILLIMITÉ',
      description: 'Laser Ariane avec capacité illimitée',
      price: '37 900,00 €',
      leaseBasePrice: 31900, // Base pour calcul location
      isConnect: false
    },
    'pam4-connect': {
      name: 'LASER ARIANE + PAM LASER 4 CONNECT',
      description: 'Laser Ariane avec tirs limités',
      price: '28 900,00 €',
      leaseBasePrice: 22900, // Base pour calcul location
      isConnect: true
    },
    'pam2-illimite': {
      name: 'LASER ARIANE + PAM LASER 2.5 ILLIMITÉ',
      description: 'Laser Ariane avec capacité illimitée',
      price: '35 900,00 €',
      leaseBasePrice: 28900, // Base pour calcul location
      isConnect: false
    },
    'pam2-connect': {
      name: 'LASER ARIANE + PAM LASER 2.5 CONNECT',
      description: 'Laser Ariane avec tirs limités',
      price: '25 500,00 €',
      leaseBasePrice: 21900, // Base pour calcul location
      isConnect: true
    }
  };

  // Machine actuellement sélectionnée (raccourci pour accès facile)
  const currentMachine = machines[selectedMachine];

  // ============================================
  // EFFETS (useEffect)
  // ============================================

  // Réinitialise le pack de tirs si on passe à un modèle non-Connect
  // (les packs de tirs ne sont disponibles que pour les modèles Connect)
  useEffect(() => {
    if (!currentMachine.isConnect) {
      setSelectedShotPack(null);
    }
  }, [selectedMachine, currentMachine.isConnect]);

  // Réinitialise le pack de flashs si aucun applicateur HR/SR n'est sélectionné
  // (le pack flash n'est pertinent que si un applicateur IPL est choisi)
  useEffect(() => {
    if (!selectedOptions.applicateurHR && !selectedOptions.applicateurSR) {
      setSelectedFlashPack(null);
    }
  }, [selectedOptions.applicateurHR, selectedOptions.applicateurSR]);

  // Réinitialise le premier loyer majoré si on passe en mode achat ou si on change de machine
  useEffect(() => {
    if (purchaseType === 'purchase') {
      setFirstLeasePayment(0);
    }
  }, [purchaseType]);

  useEffect(() => {
    // Réinitialiser le premier loyer majoré si on change de machine
    setFirstLeasePayment(0);
  }, [selectedMachine]);

  // Ajuster le compteur de formation Laser selon le mode
  // En location : 0 (1 personne déjà incluse), en achat : 1
  useEffect(() => {
    if (purchaseType === 'lease') {
      setFormationLaserCount(0);
    } else {
      setFormationLaserCount(1);
    }
  }, [purchaseType]);

  // ============================================
  // GESTIONNAIRES D'ÉVÉNEMENTS
  // ============================================

  /**
   * Gère le clic sur une option (checkbox)
   * @param {string} optionKey - Clé de l'option (ex: 'warranty', 'applicateurHR')
   * @param {boolean} isMonthly - true si on clique sur le bouton "mensuel"
   */
  const handleOptionToggle = (optionKey, isMonthly = false) => {
    const option = options[optionKey];
    const monthlyKey = `${optionKey}Monthly`;

    // Si l'option est incluse en location, on ne peut pas la modifier
    if (purchaseType === 'lease' && option.includedInLease) return;

    if (isMonthly) {
      // Toggle monthly option - coche aussi l'option principale si pas cochée
      setSelectedOptions(prev => ({
        ...prev,
        [optionKey]: true,
        [monthlyKey]: !prev[monthlyKey]
      }));
    } else {
      // Toggle main option
      setSelectedOptions(prev => ({
        ...prev,
        [optionKey]: !prev[optionKey],
        [monthlyKey]: !prev[optionKey] ? false : prev[monthlyKey]
      }));
    }
  };


  // ============================================
  // FONCTIONS DE CALCUL
  // ============================================

  /**
   * Calcule le total des options sélectionnées (hors packs)
   * - Ignore les options incluses en mode location
   * - Applique le multiplicateur "par personne" pour les formations
   * @returns {number} Total en euros
   */
  const calculateOptionsTotal = () => {
    let total = 0;
    Object.keys(options).forEach(key => {
      const option = options[key];

      // Cas spécial : formation Laser en location (1 personne incluse)
      if (purchaseType === 'lease' && key === 'formationLaser') {
        // Ne facturer que les personnes supplémentaires
        if (formationLaserCount > 0) {
          total += option.price * formationLaserCount;
        }
        return;
      }

      // Skip si l'option est incluse en location
      if (purchaseType === 'lease' && option.includedInLease) return;

      if (selectedOptions[key]) {
        const monthlyKey = `${key}Monthly`;
        if (option.hasMonthlyOption && selectedOptions[monthlyKey]) {
          // Option payée en mensualités : n'ajoute rien au total comptant
          total += option.monthlyPrice;
        } else {
          // Option payée comptant (multiplié par le nombre de personnes si formation)
          let multiplier = 1;
          if (option.perPerson) {
            multiplier = key === 'formationLaser' ? formationLaserCount : formationIPLCount;
          }
          total += option.price * multiplier;
        }
      }
    });
    return total;
  };

  /**
   * Calcule le montant mensuel total des options payées en mensualités
   * (affiché comme "+XX €/mois pendant 59 mois")
   * @returns {number} Total mensuel en euros
   */
  const getMonthlyOptionsTotal = () => {
    let total = 0;
    Object.keys(options).forEach(key => {
      const option = options[key];
      if (purchaseType === 'lease' && option.includedInLease) return;

      if (selectedOptions[key] && option.hasMonthlyOption) {
        const monthlyKey = `${key}Monthly`;
        if (selectedOptions[monthlyKey]) {
          total += option.monthlyPrice;
        }
      }
    });
    return total;
  };

  /**
   * Calcule le premier versement en mode location (hors premier loyer majoré)
   * Inclut : options payées comptant + pack de tirs + pack de flashs
   * @returns {number} Total du premier versement en euros
   */
  const getFirstPaymentTotal = () => {
    let total = 0;
    Object.keys(options).forEach(key => {
      const option = options[key];

      // Cas spécial : formation Laser en location (1 personne incluse)
      // Les personnes supplémentaires sont facturées au premier versement
      if (purchaseType === 'lease' && key === 'formationLaser') {
        if (formationLaserCount > 0) {
          total += option.price * formationLaserCount;
        }
        return;
      }

      if (purchaseType === 'lease' && option.includedInLease) return;

      if (selectedOptions[key]) {
        const monthlyKey = `${key}Monthly`;
        let multiplier = 1;
        if (option.perPerson) {
          multiplier = key === 'formationLaser' ? formationLaserCount : formationIPLCount;
        }
        if (option.hasMonthlyOption && !selectedOptions[monthlyKey]) {
          // Option avec option mensuelle mais payée comptant
          total += option.price * multiplier;
        } else if (!option.hasMonthlyOption) {
          // Option sans option mensuelle (toujours comptant)
          total += option.price * multiplier;
        }
      }
    });
    // Ajouter le pack de tirs et le pack de flashs au premier versement en location
    if (purchaseType === 'lease') {
      total += calculateShotPackPrice();
      total += calculateFlashPackPrice();
    }
    return total;
  };

  /**
   * Vérifie si un premier loyer majoré est défini
   * @returns {boolean} true si premier loyer majoré > 0
   */
  const hasFirstLeasePayment = () => {
    return purchaseType === 'lease' && firstLeasePayment > 0;
  };

  /**
   * Vérifie si au moins une option est payée en mensualités
   * @returns {boolean} true si des mensualités sont actives
   */
  const hasMonthlyOptions = () => {
    return Object.keys(options).some(key => {
      const option = options[key];
      if (purchaseType === 'lease' && option.includedInLease) return false;
      const monthlyKey = `${key}Monthly`;
      return selectedOptions[key] && option.hasMonthlyOption && selectedOptions[monthlyKey];
    });
  };

  /**
   * Vérifie si au moins une option (hors applicateurs) est payée en mensualités
   * Utilisé pour l'affichage dans la barre de résumé (les applicateurs sont masqués)
   * @returns {boolean} true si des mensualités sont actives (hors applicateurs)
   */
  const hasMonthlyOptionsForSummary = () => {
    return Object.keys(options).some(key => {
      // Exclure les applicateurs de l'affichage résumé
      if (['applicateurHR', 'applicateurSR'].includes(key)) return false;
      const option = options[key];
      if (purchaseType === 'lease' && option.includedInLease) return false;
      const monthlyKey = `${key}Monthly`;
      return selectedOptions[key] && option.hasMonthlyOption && selectedOptions[monthlyKey];
    });
  };

  /**
   * Calcule le montant mensuel des options hors applicateurs (pour la barre de résumé)
   * @returns {number} Total mensuel en euros (hors applicateurs)
   */
  const getMonthlyOptionsTotalForSummary = () => {
    let total = 0;
    Object.keys(options).forEach(key => {
      // Exclure les applicateurs
      if (['applicateurHR', 'applicateurSR'].includes(key)) return;
      const option = options[key];
      if (purchaseType === 'lease' && option.includedInLease) return;

      if (selectedOptions[key] && option.hasMonthlyOption) {
        const monthlyKey = `${key}Monthly`;
        if (selectedOptions[monthlyKey]) {
          total += option.monthlyPrice;
        }
      }
    });
    return total;
  };

  /**
   * Vérifie s'il y a un premier versement à afficher (mode location uniquement)
   * @returns {boolean} true si un premier versement existe
   */
  const hasFirstPayment = () => {
    if (purchaseType !== 'lease') return false;
    return getFirstPaymentTotal() > 0;
  };

  /**
   * Calcule le prix du pack de tirs laser sélectionné
   * @returns {number} Prix du pack ou 0 si aucun sélectionné
   */
  const calculateShotPackPrice = () => {
    if (!selectedShotPack || !currentMachine.isConnect) return 0;
    return shotPacks[selectedShotPack].price;
  };

  /**
   * Calcule le prix du pack de flashs IPL sélectionné
   * @returns {number} Prix du pack ou 0 si aucun sélectionné ou aucun applicateur
   */
  const calculateFlashPackPrice = () => {
    if (!selectedFlashPack) return 0;
    if (!selectedOptions.applicateurHR && !selectedOptions.applicateurSR) return 0;
    return flashPacks[selectedFlashPack].price;
  };

  /**
   * Mensualité de location calculée selon le barème PEAC
   * @param {string} duration - '48' ou '60'
   * @param {number} firstPayment - Premier loyer majoré (0 si aucun)
   * @returns {number} Mensualité en euros
   */
  const getLeaseMonthly = (duration, firstPayment = 0) =>
    computePeacMonthly(currentMachine.leaseBasePrice, parseInt(duration), firstPayment);

  /**
   * PLM maximum autorisé par PEAC (30 % du montant financé)
   * @returns {number} Montant maximum en euros
   */
  const getMaxFirstLeasePayment = () => Math.floor(currentMachine.leaseBasePrice * PLM_MAX_RATIO);

  /**
   * Retourne la durée effective de location
   * Si PLM défini : 47 ou 59 mois, sinon 48 ou 60 mois
   * @returns {number} Durée en mois
   */
  const getEffectiveLeaseDuration = () => {
    if (firstLeasePayment > 0) {
      return leaseDuration === '48' ? 47 : 59;
    }
    return parseInt(leaseDuration);
  };

  /**
   * Récupère le prix de base de la machine (achat ou mensualité location)
   * En location, si un premier loyer majoré est défini, il s'agit des loyers
   * suivants (47 ou 59 mois)
   * @returns {number} Prix de base en euros
   */
  const getBasePrice = () => {
    if (purchaseType === 'purchase') {
      // Achat : prix comptant de la machine
      return parseFloat(currentMachine.price.replace(/\s/g, '').replace(',', '.').replace('€', ''));
    }
    // Location : barème PEAC, avec ou sans premier loyer majoré
    return getLeaseMonthly(leaseDuration, firstLeasePayment);
  };

  /**
   * Formate un prix en euros avec séparateurs français
   * Ex: 39900.00 => "39 900,00"
   * @param {number} price - Prix à formater
   * @returns {string} Prix formaté
   */
  const formatPrice = (price) => {
    const [integerPart, decimalPart] = price.toFixed(2).split('.');
    const formattedInteger = integerPart.replace(/\B(?=(\d{3})+(?!\d))/g, ' ');
    return `${formattedInteger},${decimalPart}`;
  };

  /**
   * Calcule et formate le prix total à afficher
   * - Achat : prix machine + options + packs
   * - Location : mensualité + options mensuelles
   * @returns {string} Prix formaté avec unité (€ ou €/mois)
   */
  const getTotalPrice = () => {
    const basePrice = getBasePrice();
    const shotPackPrice = calculateShotPackPrice();
    const flashPackPrice = calculateFlashPackPrice();

    if (purchaseType === 'purchase') {
      // Achat : additionne tout (machine + options + packs)
      const optionsTotal = calculateOptionsTotal();
      const total = basePrice + optionsTotal + shotPackPrice + flashPackPrice;
      return formatPrice(total) + ' €';
    } else {
      // Location : mensualité de base + options mensuelles
      // (les packs sont ajoutés au premier versement, pas à la mensualité)
      const monthlyOptions = getMonthlyOptionsTotal();
      const total = basePrice + monthlyOptions;
      return formatPrice(total) + ' €/mois';
    }
  };

  /**
   * Retourne le prix à afficher (alias de getTotalPrice)
   * @returns {string} Prix formaté
   */
  const getDisplayPrice = () => {
    return getTotalPrice();
  };

  // ============================================
  // RENDU JSX
  // ============================================

  return (
    <div className="configurator-ariane">
      {/* Bandeau promotionnel */}
      <div className="promo-banner">
        Disponible à partir de 470,00 €/mois
      </div>

      {/* Header avec retour */}
      <header className="configurator-header">
        <Link to="/" className="back-button">← Retour</Link>
        <h2>Configurateur LASER ARIANE</h2>
      </header>

      {/*
        Layout principal en 2 colonnes :
        - Gauche : Galerie d'images/vidéos du produit
        - Droite : Configurateur avec les étapes de personnalisation
      */}
      <div className="configurator-layout">
        {/* ===== COLONNE GAUCHE : Galerie médias ===== */}
        <div className="image-section">
          <div className="image-container">
            {media.map((item, index) => (
              item.type === 'video' ? (
                <iframe
                  key={index}
                  src={item.url}
                  title="LASER ARIANE Video"
                  className={`machine-video ${index === currentImageIndex ? 'active' : ''}`}
                  frameBorder="0"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                />
              ) : (
                <img
                  key={index}
                  src={item.url}
                  alt={`LASER ARIANE ${index + 1}`}
                  className={`machine-image ${index === currentImageIndex ? 'active' : ''}`}
                />
              )
            ))}

            {/* Indicateurs de galerie */}
            <div className="gallery-indicators">
              {media.map((_, index) => (
                <button
                  key={index}
                  className={`indicator ${index === currentImageIndex ? 'active' : ''}`}
                  onClick={() => setCurrentImageIndex(index)}
                  aria-label={`Média ${index + 1}`}
                />
              ))}
            </div>
          </div>
        </div>

        {/* ===== COLONNE DROITE : Configurateur scrollable ===== */}
        <div className="config-section">
          <div className="config-content">
            {/* Section 1: Choix du modèle */}
            <section className="config-step">
              <h3 className="step-title">1. Choisissez votre modèle</h3>
              <div className="options-grid">
                {Object.entries(machines).map(([key, machine]) => (
                  <button
                    key={key}
                    className={`option-card ${selectedMachine === key ? 'selected' : ''}`}
                    onClick={() => setSelectedMachine(key)}
                  >
                    <h4>{machine.name}</h4>
                    <p>{machine.description}</p>
                    <span className="option-price">{machine.price}</span>
                  </button>
                ))}
              </div>
              {currentMachine.isConnect && (
                <div className="connect-info-notice">
                  <p>L'applicateur laser Connect est mis à la disposition du Client dans le cadre d'un contrat d'usage Connect défini par Eurofeedback. L'applicateur demeure la propriété exclusive de Eurofeedback - sauf si souscription à l'option Liberty (voir modalité ci-dessous).</p>
                </div>
              )}
            </section>

            {/* ----- Section 2 : Mode d'acquisition (Achat ou Location) ----- */}
            <section className="config-step">
              <h3 className="step-title">2. Mode d'acquisition</h3>
              <div className="toggle-group">
                <button
                  className={`toggle-button ${purchaseType === 'purchase' ? 'active' : ''}`}
                  onClick={() => setPurchaseType('purchase')}
                >
                  <div className="toggle-icon">💳</div>
                  <div className="toggle-label">Achat</div>
                  <div className="toggle-description">Propriété immédiate</div>
                </button>
                <button
                  className={`toggle-button ${purchaseType === 'lease' ? 'active' : ''}`}
                  onClick={() => setPurchaseType('lease')}
                >
                  <div className="toggle-icon">📅</div>
                  <div className="toggle-label">Location</div>
                  <div className="toggle-description">Flexibilité financière</div>
                </button>
              </div>
            </section>

            {/* ----- Section 3 : Durée de location (visible uniquement en mode Location) ----- */}
            {purchaseType === 'lease' && (
              <section className="config-step lease-duration">
                <h3 className="step-title">3. Durée de location</h3>
                <div className="toggle-group">
                  <button
                    className={`toggle-button ${leaseDuration === '48' ? 'active' : ''}`}
                    onClick={() => setLeaseDuration('48')}
                  >
                    <div className="toggle-label">48 mois</div>
                    <div className="toggle-price">{formatPrice(getLeaseMonthly('48'))} €/mois</div>
                    <div className="toggle-description">4 ans de location</div>
                  </button>
                  <button
                    className={`toggle-button ${leaseDuration === '60' ? 'active' : ''}`}
                    onClick={() => setLeaseDuration('60')}
                  >
                    <div className="toggle-label">60 mois</div>
                    <div className="toggle-price">{formatPrice(getLeaseMonthly('60'))} €/mois</div>
                    <div className="toggle-description">5 ans de location</div>
                  </button>
                </div>

                {/* Premier loyer majoré */}
                <div className="first-lease-payment">
                  <label className="first-lease-label">Premier loyer majoré (optionnel)</label>
                  <p className="first-lease-description">
                    Versé au démarrage, il réduit les loyers suivants (30 % maximum du montant financé).
                  </p>
                  <div className="first-lease-input-container">
                    <input
                      type="number"
                      className="first-lease-input"
                      value={firstLeasePayment || ''}
                      onChange={(e) => {
                        const value = parseInt(e.target.value) || 0;
                        // Limiter au PLM maximum autorisé par PEAC
                        const maxValue = getMaxFirstLeasePayment();
                        setFirstLeasePayment(Math.min(Math.max(0, value), maxValue));
                      }}
                      placeholder="0"
                      min="0"
                      max={getMaxFirstLeasePayment()}
                      step="500"
                    />
                    <span className="first-lease-currency">€</span>
                  </div>
                  {firstLeasePayment > 0 && (
                    <p className="first-lease-result">
                      Nouvelle mensualité : <strong>{formatPrice(getBasePrice())} €/mois sur {getEffectiveLeaseDuration()} mois</strong>
                    </p>
                  )}
                </div>
              </section>
            )}


            {/*
              ----- Section Options -----
              Options générales : garantie, installation, formations
              Les applicateurs IPL (HR, SR, embout) sont affichés dans une section séparée
            */}
            <section className="config-step">
              <h3 className="step-title">
                {purchaseType === 'lease' ? '4. Options' : '3. Options'}
              </h3>
              <div className="options-list">
                {/* Filtre pour exclure les applicateurs IPL (affichés dans une autre section) */}
                {Object.entries(options)
                  .filter(([key]) => !['applicateurHR', 'applicateurSR', 'emboutIPLV64'].includes(key))
                  .map(([key, option]) => {
                    const isIncludedInLease = purchaseType === 'lease' && option.includedInLease;
                    // Formations mutuellement exclusives
                    const isDisabledByOtherFormation =
                      (key === 'formationLaser' && selectedOptions.formationLaserIPL) ||
                      (key === 'formationLaserIPL' && selectedOptions.formationLaser);

                    return (
                      <div
                        key={key}
                        className={`option-item ${isIncludedInLease ? 'included' : ''} ${
                          selectedOptions[key] || isIncludedInLease ? 'selected' : ''
                        } ${isDisabledByOtherFormation ? 'disabled' : ''}`}
                        onClick={() => !isIncludedInLease && !isDisabledByOtherFormation && handleOptionToggle(key, false)}
                      >
                        <div className="option-checkbox">
                          <input
                            type="checkbox"
                            checked={isIncludedInLease || selectedOptions[key]}
                            disabled={isIncludedInLease || isDisabledByOtherFormation}
                            onChange={() => {}}
                          />
                        </div>
                        <div className="option-info">
                          <div className="option-name-container">
                            <div className="option-name">{option.name}</div>
                            {isDisabledByOtherFormation && (
                              <div className="option-disabled-note">Non cumulable avec l'autre formation</div>
                            )}
                          </div>
                          <div className="option-price-container">
                            {/* Afficher le prix uniquement si ce n'est pas une formation (perPerson) */}
                            {!option.perPerson && (
                              <div className="option-price">{formatPrice(option.price)} €</div>
                            )}
                            {/* Pour les formations : afficher le sélecteur de personnes avec le calcul */}
                            {option.perPerson && (
                              <div className="person-count-selector" onClick={(e) => e.stopPropagation()}>
                                {/* En location, 1 personne incluse pour formation Laser */}
                                {purchaseType === 'lease' && key === 'formationLaser' && (
                                  <div className="person-included-note">1 personne incluse</div>
                                )}
                                <div className="person-count-row">
                                  <label>{purchaseType === 'lease' && key === 'formationLaser' ? 'Personnes supplémentaires' : 'Nombre de personnes'}</label>
                                  <select
                                    value={key === 'formationLaser' ? formationLaserCount : formationIPLCount}
                                    onChange={(e) => key === 'formationLaser'
                                      ? setFormationLaserCount(parseInt(e.target.value))
                                      : setFormationIPLCount(parseInt(e.target.value))
                                    }
                                  >
                                    {(purchaseType === 'lease' && key === 'formationLaser'
                                      ? [0, 1, 2, 3, 4, 5, 6, 7, 8, 9]
                                      : [1, 2, 3, 4, 5, 6, 7, 8, 9, 10]
                                    ).map(n => (
                                      <option key={n} value={n}>{n}</option>
                                    ))}
                                  </select>
                                </div>
                                <div className="person-price-row">
                                  {purchaseType === 'lease' && key === 'formationLaser' ? (
                                    formationLaserCount > 0 ? (
                                      <>{formatPrice(option.price)} €/pers. × {formationLaserCount} = <strong>{formatPrice(option.price * formationLaserCount)} €</strong></>
                                    ) : (
                                      <strong>Inclus</strong>
                                    )
                                  ) : (
                                    <>{formatPrice(option.price)} €/pers. × {key === 'formationLaser' ? formationLaserCount : formationIPLCount} = <strong>{formatPrice(option.price * (key === 'formationLaser' ? formationLaserCount : formationIPLCount))} €</strong></>
                                  )}
                                </div>
                              </div>
                            )}
                            {option.hasMonthlyOption && !isIncludedInLease && (
                              <div
                                className={`monthly-toggle ${selectedOptions[`${key}Monthly`] ? 'active' : ''}`}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleOptionToggle(key, true);
                                }}
                              >
                                <input
                                  type="checkbox"
                                  checked={selectedOptions[`${key}Monthly`]}
                                  onChange={() => {}}
                                />
                                <span>{option.monthlyPrice} €/mois</span>
                              </div>
                            )}
                            {isIncludedInLease && (
                              <div className="option-included-badge">Inclus</div>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })}
              </div>
              <div className="options-footnotes">
                <p>** L'extension de garantie couvre l'appareil pour une durée de 5 ans : (2) + 3 ans (l'applicateur n'est pas inclus dans l'extension de garantie).</p>
              </div>
            </section>

            {/*
              ----- Section Pack de tirs laser -----
              Visible uniquement pour les modèles CONNECT (tirs limités)
              Permet d'acheter des tirs supplémentaires
            */}
            {currentMachine.isConnect && (
              <section className="config-step">
                <h3 className="step-title">
                  {purchaseType === 'lease' ? '5. Pack de tirs laser' : '4. Pack de tirs laser'}
                </h3>
                <div className="shot-pack-info">
                  <p>L'applicateur Laser Ariane {selectedMachine.includes('pam2') ? '2.5' : '4'} Connect (avec tirs limités) est livré avec 5 000 tirs inclus.</p>
                </div>
                <div className="shot-pack-selector">
                  <label className="shot-pack-label">Pack de tirs supplémentaires</label>
                  <select
                    className="shot-pack-dropdown"
                    value={selectedShotPack || ''}
                    onChange={(e) => setSelectedShotPack(e.target.value || null)}
                  >
                    <option value="">Sélectionnez un pack de tirs</option>
                    {Object.entries(shotPacks).map(([key, pack]) => (
                      <option key={key} value={key}>
                        {pack.name} - {formatPrice(pack.price)} €
                      </option>
                    ))}
                  </select>
                </div>
              </section>
            )}

            {/*
              ----- Section Applicateur IPL -----
              Options IPL : Applicateur HR, Applicateur SR, Embout petite zone
              Le menu "Pack flash initial" apparaît si HR ou SR est coché
            */}
            <section className="config-step">
              <h3 className="step-title">
                {purchaseType === 'lease'
                  ? (currentMachine.isConnect ? '6. Applicateur IPL en option' : '5. Applicateur IPL en option')
                  : (currentMachine.isConnect ? '5. Applicateur IPL en option' : '4. Applicateur IPL en option')
                }
              </h3>

              <div className="options-list">
                {/* Affiche uniquement les applicateurs IPL (HR, SR, embout V64) */}
                {Object.entries(options)
                  .filter(([key]) => ['applicateurHR', 'applicateurSR', 'emboutIPLV64'].includes(key))
                  .map(([key, option]) => {
                    const isIncludedInLease = purchaseType === 'lease' && option.includedInLease;

                    return (
                      <div
                        key={key}
                        className={`option-item ${isIncludedInLease ? 'included' : ''} ${
                          selectedOptions[key] || isIncludedInLease ? 'selected' : ''
                        }`}
                        onClick={() => !isIncludedInLease && handleOptionToggle(key, false)}
                      >
                        <div className="option-checkbox">
                          <input
                            type="checkbox"
                            checked={isIncludedInLease || selectedOptions[key]}
                            disabled={isIncludedInLease}
                            onChange={() => {}}
                          />
                        </div>
                        <div className="option-info">
                          <div className="option-name">{option.name}</div>
                          {option.description && (
                            <div className="option-description">{option.description}</div>
                          )}
                          <div className="option-price-container">
                            <div className="option-price">{formatPrice(option.price)} €</div>
                            {option.hasMonthlyOption && !isIncludedInLease && (
                              <div
                                className={`monthly-toggle ${selectedOptions[`${key}Monthly`] ? 'active' : ''}`}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleOptionToggle(key, true);
                                }}
                              >
                                <input
                                  type="checkbox"
                                  checked={selectedOptions[`${key}Monthly`]}
                                  onChange={() => {}}
                                />
                                <span>{option.monthlyPrice} €/mois</span>
                              </div>
                            )}
                            {isIncludedInLease && (
                              <div className="option-included-badge">Inclus</div>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })}
              </div>

              {/* Pack flash initial - visible si HR ou SR sélectionné */}
              {(selectedOptions.applicateurHR || selectedOptions.applicateurSR) && (
                <div className="flash-pack-selector">
                  <label className="flash-pack-label">Pack flash initial</label>
                  <select
                    className="flash-pack-dropdown"
                    value={selectedFlashPack || ''}
                    onChange={(e) => setSelectedFlashPack(e.target.value || null)}
                  >
                    <option value="">Sélectionnez un pack de flashs</option>
                    {Object.entries(flashPacks).map(([key, pack]) => (
                      <option key={key} value={key}>
                        {pack.name} - {formatPrice(pack.price)} €
                      </option>
                    ))}
                  </select>
                </div>
              )}

              <div className="options-footnotes">
                <p>*** Les applicateurs HR et SR legacy sont mis à la disposition du Client dans le cadre d'un contrat d'usage Connect (cf Annexe Contrat de mise à disposition de matériel Eurofeedback). Les applicateurs demeurent en toutes circonstances la propriété exclusive de Eurofeedback. En contrepartie de cette mise à disposition, le Client s'acquitte uniquement des frais d'activation de l'applicateur. Les applicateurs HR et SR sont livrés avec 1 000 flashs inclus. Les lunettes opérateurs et clients HR et SR sont comprises avec l'applicateur.</p>
              </div>
            </section>

            {/* ----- Section Pack accessoires (offert avec la machine) ----- */}
            <section className="config-step pack-accessories">
              <div className="pack-header">
                <h3 className="step-title">Pack Accessoires</h3>
                <div className="pack-badge-offered">OFFERT</div>
              </div>
              <div className="pack-content">
                {currentMachine.isConnect ? (
                  <ul className="pack-items-list">
                    <li>GEL INCOLORE 5 L</li>
                    <li>MARQUEUR BLANC LASER & IPL</li>
                    <li>LUNETTES OPERATEUR LASER</li>
                    <li>LUNETTES COQUE CLIENT LASER</li>
                    <li>PACK MARKETING</li>
                    <li>PACK TIRS LASER ARIANE 5K</li>
                  </ul>
                ) : (
                  <ul className="pack-items-list">
                    <li>GEL INCOLORE 5 L</li>
                    <li>MARQUEUR BLANC LASER & IPL</li>
                    <li>LUNETTES OPERATEUR LASER</li>
                    <li>LUNETTES COQUE CLIENT LASER</li>
                    <li>PACK MARKETING</li>
                  </ul>
                )}
                <div className="pack-value">
                  <span className="pack-value-label">Valeur:</span>
                  <span className="pack-value-price">{currentMachine.isConnect ? '984,00 €' : '534,00 €'}</span>
                </div>
              </div>
            </section>

            {/*
              ----- Option Liberty (modèles Connect uniquement) -----
              Permet de passer d'un modèle Connect (tirs limités) à un modèle illimité
              moyennant un paiement dégressif selon l'ancienneté
            */}
            {currentMachine.isConnect && (
              <section className="config-step connect-disclaimer">
                <div className="disclaimer-content">
                  <div className="liberty-option">
                    <h4>Option Liberty</h4>
                    <p>À l'issue d'une période d'un (1) an suivant la date d'acquisition de l'appareil, le Client a la possibilité de faire lever le mode limité de son appareil et de passer en mode Liberty, sous réserve du paiement d'un montant déterminé ci-dessous :</p>
                    <ul className="liberty-prices">
                      <li><span>Après 1 an</span><strong>9 000 €</strong></li>
                      <li><span>Après 2 ans</span><strong>7 000 €</strong></li>
                      <li><span>Après 3 ans</span><strong>5 000 €</strong></li>
                      <li><span>Après 4 ans</span><strong>3 000 €</strong></li>
                      <li><span>Après 5 ans</span><strong>1 000 €</strong></li>
                    </ul>
                  </div>
                </div>
              </section>
            )}

            {/* Espace pour scroll */}
            <div style={{ height: '100px' }}></div>
          </div>
        </div>
      </div>

      {/*
        ===== BARRE DE RÉSUMÉ STICKY =====
        Affiche le prix total/mensualité en bas de l'écran
        Cliquable pour afficher/masquer le détail des prix
      */}
      <div className="config-summary-sticky">
        <div className="summary-bar" onClick={() => setIsDetailsExpanded(!isDetailsExpanded)}>
          <div className="summary-main">
            {/* Prix principal : total (achat) ou mensualité (location) */}
            <span className="summary-price-label">
              {purchaseType === 'purchase' ? 'Prix total' : 'Mensualité'}
            </span>
            <span className="summary-price-value">{getDisplayPrice()}</span>

            {/* Affiche les mensualités d'options si présentes (achat comptant) - hors applicateurs */}
            {purchaseType === 'purchase' && hasMonthlyOptionsForSummary() && (
              <span className="summary-monthly-note">
                + {getMonthlyOptionsTotalForSummary()} €/mois sur 59 mois
              </span>
            )}

            {/* Affiche les mensualités d'options si présentes (location) - hors applicateurs */}
            {purchaseType === 'lease' && hasMonthlyOptionsForSummary() && (
              <span className="summary-monthly-note">
                + {getMonthlyOptionsTotalForSummary()} €/mois pendant 59 mois
              </span>
            )}

            {/* Affiche le premier loyer majoré si présent (location uniquement) */}
            {hasFirstLeasePayment() && (
              <span className="summary-monthly-note summary-first-lease">
                Premier loyer majoré : {formatPrice(firstLeasePayment)} €
              </span>
            )}

            {/* Affiche le premier versement si présent (location uniquement) */}
            {purchaseType === 'lease' && hasFirstPayment() && (
              <span className="summary-monthly-note">
                + {formatPrice(getFirstPaymentTotal())} € en premier versement
              </span>
            )}
          </div>
          <button className="expand-toggle">
            <span className={`arrow ${isDetailsExpanded ? 'up' : 'down'}`}>▼</span>
          </button>
        </div>

        {isDetailsExpanded && (
          <div className="summary-details-expanded">
            <div className="summary-item">
              <span className="summary-label">Modèle:</span>
              <span className="summary-value">{currentMachine.name}</span>
            </div>
            <div className="summary-item">
              <span className="summary-label">Mode:</span>
              <span className="summary-value">
                {purchaseType === 'purchase' ? 'Achat' : 'Location'}
              </span>
            </div>
            {purchaseType === 'lease' && (
              <div className="summary-item">
                <span className="summary-label">Durée:</span>
                <span className="summary-value">{getEffectiveLeaseDuration()} mois</span>
              </div>
            )}
            {hasFirstLeasePayment() && (
              <div className="summary-item summary-first-lease-item">
                <span className="summary-label">Premier loyer majoré:</span>
                <span className="summary-value">{formatPrice(firstLeasePayment)} €</span>
              </div>
            )}
            {currentMachine.isConnect && selectedShotPack && (
              <div className="summary-item">
                <span className="summary-label">Pack tirs:</span>
                <span className="summary-value">
                  {shotPacks[selectedShotPack].name} ({formatPrice(shotPacks[selectedShotPack].price)} €)
                </span>
              </div>
            )}
            {purchaseType === 'purchase' && calculateOptionsTotal() > 0 && (
              <div className="summary-item">
                <span className="summary-label">Options:</span>
                <span className="summary-value">
                  {formatPrice(calculateOptionsTotal())} €
                  {hasMonthlyOptions() && (
                    <span className="summary-monthly-detail"> (+ {getMonthlyOptionsTotal()} €/mois)</span>
                  )}
                </span>
              </div>
            )}
            {purchaseType === 'lease' && (
              <>
                <div className="summary-item">
                  <span className="summary-label">Options incluses:</span>
                  <span className="summary-value">Oui</span>
                </div>
                {hasMonthlyOptions() && (
                  <div className="summary-item">
                    <span className="summary-label">Options mensuelles:</span>
                    <span className="summary-value">+ {getMonthlyOptionsTotal()} €/mois</span>
                  </div>
                )}
                {hasFirstPayment() && (
                  <div className="summary-item">
                    <span className="summary-label">Premier versement:</span>
                    <span className="summary-value">{formatPrice(getFirstPaymentTotal())} €</span>
                  </div>
                )}
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

export default ConfiguratorAriane;
